/**
 * PressurePlate — depresses when a player stands on it.
 *
 * Uses physics overlap detection with player aggregates.
 * Basil's Pneumatic Anchor is the natural way to hold it down,
 * but any player body resting on it counts.
 *
 * Emits PRESSURE_PLATE_ACTIVATED / PRESSURE_PLATE_DEACTIVATED events.
 */
import { Scene } from "@babylonjs/core/scene";
import { Vector3 } from "@babylonjs/core/Maths/math.vector";
import { AbstractMesh } from "@babylonjs/core/Meshes/abstractMesh";
import { MeshBuilder } from "@babylonjs/core/Meshes/meshBuilder";
import { Mesh } from "@babylonjs/core/Meshes/mesh";
import { TransformNode } from "@babylonjs/core/Meshes/transformNode";
import { StandardMaterial } from "@babylonjs/core/Materials/standardMaterial";
import { PBRMaterial } from "@babylonjs/core/Materials/PBR/pbrMaterial";
import { Color3 } from "@babylonjs/core/Maths/math.color";
import { Animation } from "@babylonjs/core/Animations/animation";
import { PhysicsAggregate } from "@babylonjs/core/Physics/v2/physicsAggregate";
import { PhysicsShapeType } from "@babylonjs/core/Physics/v2/IPhysicsEnginePlugin";
import { InteractiveObject, InteractiveConfig } from "./InteractiveObject";
import type { InstantiatedModel } from "../../systems/AssetManager";
import { gameEvents } from "../../core/EventBus";
import { GameEvents } from "../../config/events";
import { COLORS } from "../../config/GameConfig";
import { registerHoldStation } from "../../systems/navigation/HoldStations";

export interface PressurePlateConfig extends Omit<InteractiveConfig, "size"> {
  /** Override default plate dimensions. */
  size?: Vector3;
  /** Optional visual style override for special plates, e.g. Level 2's master valve. */
  visualVariant?: "plate" | "lever";
}

export class PressurePlate extends InteractiveObject {
  private isPressed = false;
  private plateMesh: Mesh;
  private plateAggregate: PhysicsAggregate;
  private trimMat: StandardMaterial | null = null;
  private glowMat: StandardMaterial | null = null;

  /** Visual roots of the loaded GLB override (pressure_plate.glb).
   *  `loadModelOverride` hides the procedural `plateMesh` and parents
   *  these under `rootMesh` — so the press/release sink animation must
   *  ALSO target them or the visible art never moves (Q-039: "plate
   *  sink cue carried by glow alone"). */
  private modelVisualRoots: TransformNode[] = [];
  /** Authored local Y of each visual root, captured once at model load.
   *  Sink keys are RELATIVE deltas from this baseline — never absolute
   *  0-based targets, which would teleport shared-instance plates whose
   *  GLB root carries a non-zero authored local Y. */
  private modelRootBaseYs: number[] = [];

  /** Vertical distance the plate sinks when pressed. */
  /** Visible Y-axis drop when a cat stands on the plate. Increased
   *  from 0.08 → 0.2 because the Gemini QA review couldn't see the
   *  original sink at normal camera distance. */
  private readonly sinkDistance = 0.2;

  /** Overlap detection radius (horizontal) and height tolerance.
   * The vertical tolerance needs to accommodate a cat capsule whose
   * `mesh.position` is the capsule *center* while the plate's origin is
   * its center too. A cat standing on the plate settles at roughly
   * capsuleHeight/2 + capsuleRadius + plateHeight/2 ≈ 0.85 above the
   * plate root, so the window must be > 0.85 or the trigger never fires. */
  private readonly overlapRadius = 0.9;
  // Tightened from 1.1 → 0.85 so a jumping cat arcing OVER the plate
  // does not trigger press. With capsule height 1.4 the mesh origin
  // sits ~0.7 above the plate when a cat is actually standing on it,
  // so 0.85 leaves just enough margin for minor bob without admitting
  // a jump apex (typical peak: plate.y + ~1.5 to ~2.5). Gemini 3.1-pro
  // fluidity review caught this as "Basil bypasses Anchor tutorial by
  // jumping over the plate and firing pressurePlate:activated mid-air."
  private readonly overlapHeight = 0.85;

  constructor(scene: Scene, config: PressurePlateConfig) {
    const size = config.size ?? new Vector3(1.2, 0.1, 0.8);
    super(scene, { ...config, size });

    // Create the plate mesh (a flat box)
    this.plateMesh = MeshBuilder.CreateBox(
      `pressurePlate_${config.key}`,
      { width: size.x, height: size.y, depth: size.z },
      scene
    );
    this.plateMesh.parent = this.rootMesh;
    this.plateMesh.position = Vector3.Zero();

    // Material
    const mat = new StandardMaterial(`pressurePlate_${config.key}_mat`, scene);
    mat.diffuseColor = Color3.FromHexString(COLORS.COPPER);
    mat.specularColor = Color3.Black();
    mat.emissiveColor = Color3.Black();
    this.plateMesh.material = mat;
    // Keep a reference so base class state changes still work
    this.material = mat;

    if (config.visualVariant === "lever") {
      this.buildLeverAssembly(size);
    }

    // The plate does NOT need a physics body — checkPressure() detects
    // cat overlap via position math, and a solid static body here would
    // block cats from walking onto the plate surface (their capsule
    // bottoms sit at the same Y as the plate top on most levels). The
    // PhysicsAggregate was causing cats to get stuck against the plate's
    // west face on L1's staircase.
    this.plateAggregate = new PhysicsAggregate(
      this.plateMesh,
      PhysicsShapeType.BOX,
      { mass: 0, friction: 0.9, restitution: 0 },
      scene
    );
    // Disable collisions so the plate is a sensor, not a wall.
    const body = this.plateAggregate.body;
    if (body) {
      body.disablePreStep = true;
      body.setCollisionCallbackEnabled(false);
      // Havok v2: setting the shape's filter membership to 0 removes
      // it from all collision groups without disposing the body (which
      // would break the aggregate).
      const shape = body.shape;
      if (shape) {
        shape.filterMembershipMask = 0;
        shape.filterCollideMask = 0;
      }
    }

    // Attempt to load 3D model override (falls back to procedural mesh)
    if (config.visualVariant !== "lever") {
      this.loadModelOverride("/assets/models/environment/pressure_plate.glb", {
        sharedInstances: true,
      });
    }

    // Solo-mode hold station: while pressed, a cat standing on this plate
    // is there on purpose — the companion follow must not march it off.
    this.unregisterHoldStation = registerHoldStation(scene, {
      key: `pressurePlate_${config.key}`,
      contains: (pos) => this.isPressed && this.isCatOnPlate(pos),
    });
  }

  private unregisterHoldStation: (() => void) | null = null;

  dispose(): void {
    this.unregisterHoldStation?.();
    this.unregisterHoldStation = null;
    this.plateAggregate.dispose();
    this.trimMat?.dispose();
    this.glowMat?.dispose();
    super.dispose();
  }

  /**
   * Whether a cat capsule centred at `pos` is standing on this plate — the
   * same overlap test `checkPressure` uses. Lets a puzzle tell WHICH cat is
   * holding a plate (TheOrrery's sync plates latch per cat).
   */
  isOccupiedBy(pos: Vector3): boolean {
    return this.isCatOnPlate(pos);
  }

  /** Same overlap test checkPressure uses, for a single position. */
  private isCatOnPlate(playerPos: Vector3): boolean {
    const platePos = this.rootMesh.getAbsolutePosition();
    const dx = playerPos.x - platePos.x;
    const dz = playerPos.z - platePos.z;
    const horizontalDist = Math.sqrt(dx * dx + dz * dz);
    const verticalDist = playerPos.y - platePos.y;
    return (
      horizontalDist < this.overlapRadius &&
      verticalDist >= 0 &&
      verticalDist < this.overlapHeight
    );
  }

  /**
   * Check if any of the provided player meshes are standing on the plate.
   * Call this every frame from the scene's update/render loop.
   */
  checkPressure(playerMeshes: Mesh[]): boolean {
    let pressing = false;

    for (const playerMesh of playerMeshes) {
      if (this.isCatOnPlate(playerMesh.getAbsolutePosition())) {
        pressing = true;
        break;
      }
    }

    if (pressing && !this.isPressed) {
      this.press();
    } else if (!pressing && this.isPressed) {
      this.release();
    }

    return this.isPressed;
  }

  get pressed(): boolean {
    return this.isPressed;
  }

  /** Capture the GLB visual roots + their authored local Y so the sink
   *  animation can retarget the visible art (base class calls this at
   *  the end of a successful `loadModelOverride`). */
  protected override onModelLoaded(instance: InstantiatedModel): void {
    this.modelVisualRoots = instance.rootNodes;
    this.modelRootBaseYs = instance.rootNodes.map((root) => root.position.y);

    // Apply brass PBR material to all meshes so they read as a brass prop
    // instead of an untextured dark floor tile.
    const brassMat = new PBRMaterial("brassPlateMat", this.scene);
    brassMat.albedoColor = Color3.FromHexString(COLORS.BRASS);
    brassMat.metallic = 1.0;
    brassMat.roughness = 0.4;
    
    for (const mesh of instance.meshes) {
      mesh.material = brassMat;
    }
  }

  override getHighlightTarget(): AbstractMesh {
    return this.plateMesh;
  }

  // ─── Private ─────────────────────────────────────────────────────────

  private press(): void {
    this.isPressed = true;
    this.state = "active";
    this.updateMaterialForState();
    this.updateDecorativeState();

    // Animate plate sinking down
    this.animatePlateY(-this.sinkDistance, 150);

    gameEvents.emit(GameEvents.PRESSURE_PLATE_ACTIVATED, this.key);
  }

  private release(): void {
    this.isPressed = false;
    this.state = "idle";
    this.updateMaterialForState();
    this.updateDecorativeState();

    // Animate plate rising back up
    this.animatePlateY(0, 150);

    gameEvents.emit(GameEvents.PRESSURE_PLATE_DEACTIVATED, this.key);
  }

  private animatePlateY(targetY: number, durationMs: number): void {
    const fps = 30;
    const totalFrames = Math.round((durationMs / 1000) * fps);

    const anim = new Animation(
      `plateMove_${this.key}_${Date.now()}`,
      "position.y",
      fps,
      Animation.ANIMATIONTYPE_FLOAT,
      Animation.ANIMATIONLOOPMODE_CONSTANT
    );
    anim.setKeys([
      { frame: 0, value: this.plateMesh.position.y },
      { frame: totalFrames, value: targetY },
    ]);

    this.plateMesh.animations = [anim];
    this.scene.beginAnimation(this.plateMesh, 0, totalFrames, false);

    // Mirror the sink on the loaded GLB art. The model override hides
    // plateMesh, so without this the visible plate never moved. Keys are
    // relative deltas from the captured baseline (baseY + targetY), so a
    // GLB root with an authored non-zero local Y sinks in place instead
    // of teleporting to an absolute 0-based height.
    for (let i = 0; i < this.modelVisualRoots.length; i++) {
      const root = this.modelVisualRoots[i];
      const baseY = this.modelRootBaseYs[i];
      const rootAnim = new Animation(
        `plateModelMove_${this.key}_${i}_${Date.now()}`,
        "position.y",
        fps,
        Animation.ANIMATIONTYPE_FLOAT,
        Animation.ANIMATIONLOOPMODE_CONSTANT
      );
      rootAnim.setKeys([
        { frame: 0, value: root.position.y },
        { frame: totalFrames, value: baseY + targetY },
      ]);
      root.animations = [rootAnim];
      this.scene.beginAnimation(root, 0, totalFrames, false);
    }
  }

  private buildLeverAssembly(size: Vector3): void {
    this.trimMat = new StandardMaterial(`pressurePlate_${this.key}_trimMat`, this.scene);
    this.trimMat.diffuseColor = Color3.FromHexString(COLORS.BRASS);
    this.trimMat.emissiveColor = new Color3(0.08, 0.05, 0.02);
    this.trimMat.specularColor = Color3.Black();

    this.glowMat = new StandardMaterial(`pressurePlate_${this.key}_glowMat`, this.scene);
    // Pressure plates are a Basil-ability target (Pneumatic Anchor holds
    // them, weight triggers them) — canonically they read as brass
    // machinery, not Minna bioluminescence. Keeping them green made the
    // L0 Gemini fluidity review flag a "neon green ring" clashing with
    // the jewel-tone palette. Warm brass + copper accent matches
    // Basil's harness identity.
    this.glowMat.diffuseColor = Color3.FromHexString(COLORS.BRASS);
    this.glowMat.emissiveColor = Color3.FromHexString(COLORS.BRASS).scale(0.35);
    this.glowMat.specularColor = Color3.Black();
    this.glowMat.alpha = 0.82;

    const pedestal = MeshBuilder.CreateBox(
      `pressurePlate_${this.key}_pedestal`,
      { width: size.x * 0.9, height: 0.48, depth: size.z * 0.82 },
      this.scene,
    );
    pedestal.parent = this.rootMesh;
    pedestal.position = new Vector3(0, 0.22, 0);
    pedestal.material = this.trimMat;

    const valveStem = MeshBuilder.CreateCylinder(
      `pressurePlate_${this.key}_stem`,
      { height: 1.2, diameter: 0.12, tessellation: 10 },
      this.scene,
    );
    valveStem.parent = this.rootMesh;
    valveStem.position = new Vector3(0, 0.8, 0);
    valveStem.material = this.trimMat;

    const wheel = MeshBuilder.CreateTorus(
      `pressurePlate_${this.key}_wheel`,
      { diameter: 0.8, thickness: 0.08, tessellation: 18 },
      this.scene,
    );
    wheel.parent = this.rootMesh;
    wheel.position = new Vector3(0, 1.38, 0);
    wheel.rotation.x = Math.PI / 2;
    wheel.material = this.trimMat;

    for (const [index, rot] of [0, Math.PI / 3, -Math.PI / 3].entries()) {
      const spoke = MeshBuilder.CreateCylinder(
        `pressurePlate_${this.key}_spoke_${index}`,
        { height: 0.64, diameter: 0.05, tessellation: 8 },
        this.scene,
      );
      spoke.parent = this.rootMesh;
      spoke.position = new Vector3(0, 1.38, 0);
      spoke.rotation.z = rot;
      spoke.material = this.trimMat;
    }

    const dialGlow = MeshBuilder.CreateSphere(
      `pressurePlate_${this.key}_dialGlow`,
      { diameter: 0.18, segments: 8 },
      this.scene,
    );
    dialGlow.parent = this.rootMesh;
    dialGlow.position = new Vector3(0, 1.38, 0);
    dialGlow.material = this.glowMat;

    this.updateDecorativeState();
  }

  private updateDecorativeState(): void {
    if (!this.trimMat || !this.glowMat) return;

    if (this.isPressed) {
      this.trimMat.emissiveColor = new Color3(0.3, 0.18, 0.06);
      // Hot copper ember when a cat is standing on the plate.
      this.glowMat.emissiveColor = Color3.FromHexString(COLORS.COPPER).scale(0.7);
    } else {
      this.trimMat.emissiveColor = new Color3(0.08, 0.05, 0.02);
      this.glowMat.emissiveColor = Color3.FromHexString(COLORS.BRASS).scale(0.35);
    }
  }
}
